import React from "react";
import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { Quiz } from "../../types/types";
import { QuizListProps } from "../../types/screensTypes";
import WaypointCard from "../cards/WaypointCard";

// Define props for SortableItem component
interface SortableItemProps {
    id: string;
    waypoint: Quiz;
    onClick: () => void;
    onDelete: () => void;
    isLoading?: boolean;
}

// SortableItem Component (Draggable Waypoint Item)
const SortableItem: React.FC<SortableItemProps> = ({ id, waypoint, onClick, onDelete, isLoading = false }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const waypointType = waypoint.type as string;
    const position = waypointType === "STOP" ? waypoint.position - 1000 : waypoint.position;
    const backgroundColor = waypointType === "INVISIBLE" ? "#ddd" : "white";

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <WaypointCard
                name={waypoint.internalName || t("quiz")}
                position={position}
                onClick={() => {
                    if (waypointType !== "INVISIBLE") {
                        onClick();
                    }
                }}
                onDelete={onDelete}
                dragHandleProps={listeners} // Pass drag listeners to WaypointCard
                color={backgroundColor}
                isLoading={isLoading}
            />
        </div>
    );
};

// WaypointList Component (Draggable List of Waypoints)
const QuizList: React.FC<QuizListProps> = ({ quizes, setQuizes, saveQuiz, navigateToNext, deleteQuiz, isLoading }) => {
    const handleDragEnd = async (event: DragEndEvent) => {
        const token = localStorage.getItem("token");
        if (!token) return;
        const { active, over } = event;

        // Exit if no destination or item didn't change position
        if (!over || active.id === over.id) return;

        const oldIndex = quizes.findIndex((waypoint) => waypoint.id === active.id);
        const newIndex = quizes.findIndex((waypoint) => waypoint.id === over.id);

        // Reorder the waypoints
        const reorderedWaypoints = arrayMove(quizes, oldIndex, newIndex);

        // Update positions and create temporary waypoints if needed
        const updatedWaypoints = reorderedWaypoints.map((waypoint, index) => {
            const newPosition = index + 1; // Adjust position for stop waypoints

            if (waypoint.position !== newPosition) {
                const tempWaypoint = {
                    ...waypoint,
                    position: newPosition,
                };

                // Save updated quiz waypoint
                saveQuiz({
                    token,
                    trackId: waypoint.trackId,
                    quizId: waypoint.id,
                    latitude: waypoint.point.lat || 0.0,
                    longitude: waypoint.point.lng || 0.0,
                    position: newPosition,
                });

                return tempWaypoint;
            }
            return waypoint;
        });

        // Update the state
        setQuizes(updatedWaypoints);
    };

    return (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={quizes.map((wpt) => wpt.id)} strategy={verticalListSortingStrategy}>
                <div className="waypoints-list">
                    {quizes.map((waypoint) => (
                        <SortableItem
                            key={waypoint.id}
                            id={waypoint.id}
                            waypoint={waypoint}
                            onClick={() => navigateToNext(waypoint.id)}
                            onDelete={() => deleteQuiz(waypoint.id)}
                            isLoading={isLoading}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
};

export default QuizList;
